import React, { useEffect, useState } from "react";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { isRunningAsPWA } from "../../utils/pwa";
import OfflinePage from "./OfflinePage";

interface PWAOfflineGateProps {
  children: React.ReactNode;
}

/**
 * Gates the app behind an offline screen when:
 *   1. The app is installed and running as a PWA (standalone display mode).
 *   2. There is no real internet reachability (not just `navigator.onLine`).
 *
 * In a regular browser tab the gate is a no-op and renders children as-is.
 */
const PWAOfflineGate: React.FC<PWAOfflineGateProps> = ({ children }) => {
  const [isPWA, setIsPWA] = useState<boolean>(() => isRunningAsPWA());
  const { isOnline, isChecking, recheck } = useOnlineStatus();

  useEffect(() => {
    // Display mode can change at runtime (e.g. user installs the PWA while
    // the tab is open). Listen for changes so the gate activates/deactivates
    // without requiring a reload.
    const mql = window.matchMedia("(display-mode: standalone)");
    const update = () => setIsPWA(isRunningAsPWA());

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
    // Safari < 14 fallback
    mql.addListener?.(update);
    return () => mql.removeListener?.(update);
  }, []);

  if (isPWA && !isOnline) {
    return <OfflinePage onRetry={recheck} isChecking={isChecking} />;
  }

  return <>{children}</>;
};

export default PWAOfflineGate;
